import axios from "axios";
import { ChevronRight, ShieldCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function EnterGate({ onEnter }) {
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [granted, setGranted] = useState(null);
  const [booted, setBooted] = useState(false);
  const [bootPct, setBootPct] = useState(0);
  const mountedAt = useRef(Date.now());
  const hissRef = useRef(null);
  const deniedRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    hissRef.current = new Audio("/assets/gate-hiss.wav");
    hissRef.current.preload = "auto";
    hissRef.current.volume = 0.55;
    deniedRef.current = new Audio("/assets/gate-denied.wav");
    deniedRef.current.preload = "auto";
    deniedRef.current.volume = 0.5;

    // boot sequence — warm gate crest + sound banks before revealing the form
    const crest = new Promise((resolve) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = resolve;
      img.src = "/assets/dolo-logo.png";
    });
    const warm = (audio) =>
      new Promise((resolve) => {
        audio.addEventListener("canplaythrough", resolve, { once: true });
        audio.addEventListener("error", resolve, { once: true });
        audio.load();
      });
    const assetsReady = Promise.all([crest, warm(hissRef.current), warm(deniedRef.current)]);
    const minBoot = new Promise((resolve) => window.setTimeout(resolve, 1600));
    const maxBoot = new Promise((resolve) => window.setTimeout(resolve, 5000));

    let pct = 0;
    const ticker = window.setInterval(() => {
      pct = Math.min(92, pct + Math.random() * 9 + 3);
      setBootPct(Math.floor(pct));
    }, 120);

    Promise.race([Promise.all([assetsReady, minBoot]), maxBoot]).then(() => {
      window.clearInterval(ticker);
      setBootPct(100);
      window.setTimeout(() => setBooted(true), 320);
    });

    return () => {
      document.body.style.overflow = "";
      window.clearInterval(ticker);
    };
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setError("");
    if (!email.trim()) {
      setError("SIGNAL EMPTY — ENTER YOUR OFFICIAL EMAIL.");
      deniedRef.current?.play().catch(() => {});
      return;
    }
    setSubmitting(true);
    // unlock the radio deck inside this click so the station can start the moment access lands
    window.dispatchEvent(new CustomEvent("dolo-radio-unlock"));
    try {
      const { data } = await axios.post(`${API}/gate/enter`, {
        email: email.trim(),
        website: honeypot,
        elapsed_ms: Date.now() - mountedAt.current,
      });
      setGranted(data.message || "ACCESS GRANTED");
      hissRef.current?.play().catch(() => {});
      // the deck was unlocked during the submit click — start the station now
      window.dispatchEvent(new CustomEvent("dolo-radio-autostart"));
      // release the keyboard + any focus-scroll offset before the site mounts
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      window.scrollTo(0, 0);
      window.setTimeout(() => {
        sessionStorage.setItem("dolo-gate-access", "granted");
        // land flush at the top of the site
        window.scrollTo(0, 0);
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        onEnter();
        window.requestAnimationFrame(() => window.scrollTo(0, 0));
      }, 1500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "SIGNAL REJECTED — INVALID EMAIL FORMAT.");
      deniedRef.current?.play().catch(() => {});
      setSubmitting(false);
    }
  };

  return (
    <div className={`gate-overlay ${granted ? "is-granted" : ""}`} data-testid="enter-gate">
      <div className="gate-grid" aria-hidden="true" />
      <div className="gate-panel">
        <span className="gate-corner tl" aria-hidden="true" />
        <span className="gate-corner tr" aria-hidden="true" />
        <span className="gate-corner bl" aria-hidden="true" />
        <span className="gate-corner br" aria-hidden="true" />

        {granted ? (
          <div className="gate-success" data-testid="gate-success">
            <ShieldCheck size={40} />
            <h1>ACCESS GRANTED</h1>
            <p>{granted}</p>
            <span className="gate-progress"><span /></span>
          </div>
        ) : !booted ? (
          <div className="gate-boot" data-testid="gate-boot">
            <span className="gate-boot-tag">BLACK-OPS NET // SECURE UPLINK</span>
            <p className="gate-boot-line">DECRYPTING RUNNER ARCHIVE…</p>
            <div className="gate-boot-bar">
              <span style={{ width: `${bootPct}%` }} />
            </div>
            <small className="gate-boot-pct">SIGNAL INTEGRITY {bootPct}%</small>
          </div>
        ) : (
          <>
            <span className="gate-eyebrow">RESTRICTED TRANSMISSION // SECTOR 00</span>
            <div className="gate-crest" data-testid="gate-crest">
              <img src="/assets/dolo-logo.png" alt="Dolo — Wasteland Runner logo" />
              <img className="gate-crest-copy gate-crest-a" src="/assets/dolo-logo.png" alt="" aria-hidden="true" />
              <img className="gate-crest-copy gate-crest-b" src="/assets/dolo-logo.png" alt="" aria-hidden="true" />
            </div>
            <p className="gate-sub">WASTELAND RUNNER // VOL. 1 — THE MESH DECIDES WHO PASSES</p>
            <p className="gate-copy">
              Register your official email to enter the archive. Your signal is recorded in the runner registry
              before the gates open.
            </p>
            <form className="gate-form" onSubmit={submit} noValidate>
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(event) => setHoneypot(event.target.value)}
                className="gate-honeypot"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="OFFICIAL EMAIL ADDRESS"
                className="gate-input"
                data-testid="gate-email-input"
                autoComplete="email"
              />
              <button type="submit" className="gate-button" disabled={submitting} data-testid="gate-enter-button">
                {submitting ? "VERIFYING…" : "REQUEST ACCESS"} <ChevronRight size={16} />
              </button>
            </form>
            {error ? (
              <p className="gate-error" data-testid="gate-error">
                {error}
              </p>
            ) : null}
            <p className="gate-fineprint">
              <ShieldCheck size={12} /> BOT AND DRONE SIGNALS ARE PURGED FROM THE MESH AUTOMATICALLY.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
